import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { chart } from '@/theme';

/**
 * Ranking en barras horizontales (CSS puro, sin librería de gráficas).
 *
 * Es el bloque «¿quién trae las conversaciones?» del panel: cada fila lleva su barra, el valor a la
 * derecha y una segunda línea de contexto. Se hace con CSS y no con recharts porque la etiqueta de
 * cada fila es texto largo («Campaña Black Friday — conjunto A») y un gráfico de barras la recortaría;
 * además estas filas no necesitan ejes, tooltips ni ejes gemelos.
 *
 * La barra es proporcional al MÁXIMO de la lista (no al total): así la primera fila siempre se ve
 * llena y las demás se comparan de un vistazo, que es para lo que sirve un ranking.
 */

export interface RankingBarItem {
  id: string;
  label: string;
  /** Segunda línea corta: actividad, ventas, CPA… */
  hint?: string;
  /** Número que decide el largo de la barra. */
  valor: number;
  /** Cómo se pinta ese número a la derecha (ya formateado). */
  valorLabel: string;
}

export interface RankingBarsProps {
  items: RankingBarItem[];
  /** Color por posición; por defecto la paleta de gráficas (`chart.series`). */
  colores?: readonly string[];
  /** Texto cuando no hay filas (el bloque puede quedar vacío sin ser un error). */
  emptyLabel?: string;
  /** Nota al pie (ej. «se listan las 5 con más conversaciones del periodo»). */
  footer?: ReactNode;
  className?: string;
}

export function RankingBars({
  items,
  colores = chart.series,
  emptyLabel = 'Todavía no hay datos para este ranking en el periodo.',
  footer,
  className,
}: RankingBarsProps) {
  if (items.length === 0) {
    return <p className={cn('text-caption text-ink-muted', className)}>{emptyLabel}</p>;
  }

  const maximo = items.reduce((max, item) => Math.max(max, item.valor), 0);

  return (
    <div className={className}>
      <ul className="space-y-4">
        {items.map((item, indice) => {
          // 2% mínimo: una fila con 1 conversación tiene que verse como una barra, no como nada.
          const ancho = maximo > 0 ? Math.max(2, Math.round((item.valor / maximo) * 100)) : 0;

          return (
            <li key={item.id}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-body font-medium text-ink" title={item.label}>
                  {item.label}
                </span>
                <span className="tabular shrink-0 text-body font-semibold text-ink-soft">
                  {item.valorLabel}
                </span>
              </div>

              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-pill bg-surface-muted">
                <span
                  className="block h-full rounded-pill"
                  style={{
                    width: `${ancho}%`,
                    backgroundColor: colores[indice % colores.length] ?? chart.series[0],
                  }}
                />
              </div>

              {item.hint ? (
                <p className="mt-1 truncate text-micro text-ink-faint" title={item.hint}>
                  {item.hint}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>

      {footer ? <p className="mt-4 text-micro text-ink-faint">{footer}</p> : null}
    </div>
  );
}
